import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { ObjectTypes } from '@fi-shared/types';
import { IItemsListDataFetchParams, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';
import { FiLocalSearchGroupType } from '@fi-shared/fi-local-search2/fi-local-search-group-type';
import { Subject } from 'rxjs';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiLocalSearchPropertyType } from '@fi-shared/fi-local-search2/fi-local-search-property-type';

export class LocalSearchModel {
    DataIdField = 'Id';
    DataObjectTypeField = 'ObjectType';
    DataDisplayFields = ['Name'];
    DataSearchFields = ['Name'];
    SearchData: LocalSearchDataModel[] = [];
    SearchDataMap: {
        [key: string]: LocalSearchDataModel;
    } = {};
    SearchText = '';
    LastSearchTextArray = [];
    AddedObjects: ILocalSearchDataItemModel[] = [];
    ShowContentContainer = false;
    SearchDataShow: LocalSearchDataModel[];
    WildcardSearchByEnterMode = false;

    constructor(data?) {
        if (data) {
            Object.assign(this, data);
        }
    }

    ClearSearchText() {
        this.SearchText = '';
        this.LastSearchTextArray = [];
    }

    OnAddObject = null;

    addLastSearchTextItem(text: string) {
        this.LastSearchTextArray.push(text);
    }

    removeLastSearchTextItem(searchText: string) {
        this.LastSearchTextArray.splice(this.LastSearchTextArray.indexOf(searchText), 1);
    }

    addSearchGroup(groupType: FiLocalSearchGroupType, groupSettings: ILocalSearchGroupSettings) {
        const localSearchData = new LocalSearchDataModel(Object.assign(groupSettings, { GroupType: groupType }));
        this.SearchDataMap[groupType] = localSearchData;
        this.SearchData.push(localSearchData);
    }

    addSearchGroupDataStr(groupType: FiLocalSearchGroupType, dataStr: string[]) {
        this.addSearchGroupData(
            groupType,
            dataStr.map((name) => ({ Id: undefined, Name: name })),
        );
    }
    addSearchGroupData(groupType: FiLocalSearchGroupType, data: INameIdObj[]) {
        const searchGroup = this.SearchDataMap[groupType];
        searchGroup.Data.length = 0;
        data.forEach((item) => {
            searchGroup.Data.push({
                Id: item.Id,
                Name: item.Name,
                GroupType: groupType,
            });
        });
    }
    getAddedNames(groupType: FiLocalSearchGroupType) {
        return this.AddedObjects.filter((addedObject) => addedObject.GroupType === groupType).map(
            (addedObject) => addedObject.Name,
        );
    }
    getAddedIds(groupType: FiLocalSearchGroupType) {
        return this.AddedObjects.filter((addedObject) => addedObject.GroupType === groupType).map(
            (addedObject) => addedObject.Id,
        );
    }

    private _dataChanged$ = new Subject<void>();
    get dataChanged$() {
        return this._dataChanged$.asObservable();
    }
    raiseDataChanged() {
        this._dataChanged$.next();
    }

    getLightWeightObjectCollectionBase(objectType?: ObjectTypes): ILightWeightObjectCollection {
        return {
            Names: [],
            NamesStr: [],
            Creators: [],
            Tags: [],
            BusinessAreas: [],
            Roles: [],
            BusinessUnits: [],
            Areas: [],
            Clusters: [],

            EmailAddressContacts: [],
            PhoneNumberContacts: [],
            Addresses: [],
            DataFieldValues: [],
            ObjectTypes: [],
            ObjectIds: [],

            ObjectType: objectType,
            Checklists: [],
            Checkpoints: [],
            MediaBankFileExtensions: [],
        };
    }
}

export interface ILightWeightObject extends INameIdObj {
    IsSystemObject?: boolean;
    IsActivated?: boolean;
    ParentId?: number;
    Level?: number;
}

export interface ILightWeightObjectCollection {
    Names: ILocalSearchIdNameWithPicture[];
    NamesStr: string[];
    Creators: ILocalSearchIdNameWithPicture[];
    Tags: ILocalSearchIdName[];
    BusinessAreas: ILocalSearchIdName[];
    Roles: ILocalSearchIdName[];
    BusinessUnits: ILocalSearchIdNameWithPicture[];
    Areas: ILightWeightObject[];
    Clusters: ILightWeightObject[];

    EmailAddressContacts: string[];
    PhoneNumberContacts: string[];
    Addresses: string[];
    DataFieldValues: string[];
    ObjectTypes: number[];
    ObjectIds: number[];

    ObjectType: number;
    Checklists: ILocalSearchIdName[];
    Checkpoints: ILocalSearchIdName[];
    MediaBankFileExtensions: string[];
}

export interface ILocalSearchIdName {
    Id: number;
    Name: string;
}
export interface ILocalSearchIdNameWithPicture extends ILocalSearchIdName {
    PhysicalFileName?: string;
    Extension?: string;
    FileType?: number;
}

export interface ILocalSearchDataItemModel {
    Id?: number;
    Name: string;
    GroupType?: FiLocalSearchGroupType;
    ObjectType?; // todo use groupType instead of this
    IsDescription?: boolean;
}

export interface ILocalSearchGroupSettings {
    Header?: string;
    ObjectType?: ObjectTypes;
    GroupType?: FiLocalSearchGroupType;
    Single?: boolean;
    Data?: ILocalSearchDataItemModel[];
    Limit?: number;
    IsWildCardAble?: boolean;
    WildcardSearch?: boolean;
    HideCount?: boolean;
}

export class LocalSearchDataModel {
    ObjectType: any;
    GroupType: FiLocalSearchGroupType;
    Header: string;
    Data: ILocalSearchDataItemModel[] = [];
    Limit: number;
    Single: boolean;
    ShowAllItems: boolean;
    IsWildCardAble: boolean;
    WildcardSearch: boolean;
    HideCount: boolean;
    constructor(Settings: ILocalSearchGroupSettings) {
        if (Settings === undefined) Settings = {};
        this.Header = Settings.Header;
        this.ObjectType = Settings.ObjectType;
        this.GroupType = Settings.GroupType;
        this.Single = Settings.Single;
        this.Data = Settings.Data || [];
        this.Limit = Settings.Limit || 5;
        this.ShowAllItems = false;
        this.IsWildCardAble = Settings.IsWildCardAble === undefined ? true : Settings.IsWildCardAble;
        this.WildcardSearch = Settings.WildcardSearch === undefined ? false : Settings.WildcardSearch;
        this.HideCount = Settings.HideCount;
    }
}

export class LocalSearchItemModel {
    Id: number;
    Name: string;
    IsEnabled: boolean; // todo not used? refactor/remove
    ObjectType: any;
    MediaBankPhysicalFileName: string;
    MediaBankExtension: string;
    constructor(id: number, name: string, objectType, mediaBankPhysicalFileName?: string, mediaBankExtension?: string) {
        this.Id = id;
        this.Name = name;
        this.ObjectType = objectType;
        this.MediaBankPhysicalFileName = mediaBankPhysicalFileName;
        this.MediaBankExtension = mediaBankExtension;
        this.IsEnabled = true;
    }
}

export class CoreParameterModel implements IItemsListParameterBase {
    Names: string[] = [];
    Offset = 0;
    NumberOfRecordsToTake = FiAppSettings.NumberOfRecordsPerPage;
    SelectedSort: {
        ColumnName?: string;
        SortDirection?: boolean;
    } = {};
    FilterType = SideListOptionType.SharedWithMe;
    OnlyWithExtendedRights?: boolean = false;
    FetchParams?: IItemsListDataFetchParams;
}

export class BusinessUnitParameterModel extends CoreParameterModel {
    BusinessAreaIds: number[] = [];
    AreaIds: number[] = [];
    ClusterIds: number[] = [];
    Addresses: string[] = [];
    Contacts: string[] = [];
    DataFieldValues: string[] = [];
    IncludeInactiveBusinessUnits = true;
}

export class UserParameterModel extends CoreParameterModel {
    BusinessAreaIds: number[] = [];
    AreaIds: number[] = [];
    ClusterIds: number[] = [];
    BusinessUnitIds: number[] = [];
    RoleIds: number[] = [];
    Addresses: string[] = [];
    Contacts: string[] = [];
    TagIds: number[] = [];
    DataFieldValues: string[] = [];
    ContentSearchTexts: string[] = [];
    IncludeInactiveUsers: boolean = true;
    OnlyWithContactPhones: boolean;
}
